<template>
  <div class="gps-session-dashboard">
    <!-- Session Selector -->
    <div class="session-selector">
      <div class="selector-group">
        <label>Session</label>
        <select v-model="currentSessionId" @change="onSessionChange">
          <option v-for="session in sessions" :key="session.id" :value="session.id">
            {{ formatSessionLabel(session) }}
          </option>
        </select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading session data...</p>
    </div>

    <!-- Summary Cards -->
    <div v-else class="summary-cards">
      <div class="summary-card">
        <div class="card-icon bg-success">
          <i class="bi bi-geo-alt"></i>
        </div>
        <div class="card-content">
          <span class="value">{{ formatDistance(summaryStats.avgDistance) }}</span>
          <span class="label">Avg Distance</span>
        </div>
      </div>

      <div class="summary-card">
        <div class="card-icon bg-danger">
          <i class="bi bi-speedometer2"></i>
        </div>
        <div class="card-content">
          <span class="value">{{ formatDistance(summaryStats.sprintDistance) }}</span>
          <span class="label">Sprint Distance</span>
        </div>
      </div>

      <div class="summary-card">
        <div class="card-icon bg-warning">
          <i class="bi bi-lightning-charge"></i>
        </div>
        <div class="card-content">
          <span class="value">{{ formatNumber(summaryStats.highIntensity) }}</span>
          <span class="label">High Intensity</span>
        </div>
      </div>

      <div class="summary-card">
        <div class="card-icon bg-info">
          <i class="bi bi-people"></i>
        </div>
        <div class="card-content">
          <span class="value">{{ summaryStats.athleteCount }}</span>
          <span class="label">Athletes</span>
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div v-if="!loading && sessionStats.length" class="charts-section">
      <!-- Total Distance Chart -->
      <div class="chart-card chart-card-wide">
        <h4 class="chart-title">
          <i class="bi bi-bar-chart-horizontal"></i>
          Total Distance
        </h4>
        <div class="chart-container" :style="{ height: chartHeight + 'px' }">
          <canvas ref="totalDistanceCanvas"></canvas>
        </div>
      </div>

      <!-- First Half Distance Chart -->
      <div class="chart-card chart-card-wide">
        <h4 class="chart-title">
          <i class="bi bi-bar-chart-horizontal"></i>
          First Half Distance
        </h4>
        <div class="chart-container" :style="{ height: chartHeight + 'px' }">
          <canvas ref="firstHalfCanvas"></canvas>
        </div>
      </div>

      <!-- Second Half Distance Chart -->
      <div class="chart-card chart-card-wide">
        <h4 class="chart-title">
          <i class="bi bi-bar-chart-horizontal"></i>
          Second Half Distance
        </h4>
        <div class="chart-container" :style="{ height: chartHeight + 'px' }">
          <canvas ref="secondHalfCanvas"></canvas>
        </div>
      </div>

      <!-- Speed Zones Distribution Chart -->
      <div class="chart-card chart-card-wide">
        <h4 class="chart-title">
          <i class="bi bi-layers"></i>
          Speed Zones Distribution
        </h4>
        <div class="chart-legend speed-legend">
          <span class="legend-item"><span class="legend-color" style="background: #6c757d;"></span>Walking</span>
          <span class="legend-item"><span class="legend-color" style="background: #17a2b8;"></span>Jogging</span>
          <span class="legend-item"><span class="legend-color" style="background: #28a745;"></span>Running</span>
          <span class="legend-item"><span class="legend-color" style="background: #ffc107;"></span>High Speed</span>
          <span class="legend-item"><span class="legend-color" style="background: #fd7e14;"></span>Sprint</span>
          <span class="legend-item"><span class="legend-color" style="background: #dc3545;"></span>Max Sprint</span>
        </div>
        <div class="chart-container" :style="{ height: chartHeight + 'px' }">
          <canvas ref="speedZonesCanvas"></canvas>
        </div>
      </div>

      <!-- Acceleration & Deceleration Chart -->
      <div class="chart-card chart-card-wide">
        <h4 class="chart-title">
          <i class="bi bi-arrow-left-right"></i>
          Acceleration &amp; Deceleration Events
        </h4>
        <div class="chart-legend accel-legend">
          <span class="legend-item"><span class="legend-color" style="background: #667eea;"></span>Accelerations</span>
          <span class="legend-item"><span class="legend-color" style="background: #764ba2;"></span>Decelerations</span>
        </div>
        <div class="chart-container" :style="{ height: chartHeight + 'px' }">
          <canvas ref="accelDecelCanvas"></canvas>
        </div>
      </div>
    </div>

    <!-- No Data State -->
    <div v-else-if="!loading" class="empty-state">
      <i class="bi bi-graph-down"></i>
      <p>No player data available for this session</p>
    </div>
  </div>
</template>

<script>
import {
  Chart,
  BarController,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

Chart.register(
  BarController,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartDataLabels
)

export default {
  name: 'GPSSessionDashboard',

  props: {
    sessions: {
      type: Array,
      required: true,
      default: () => []
    },
    selectedSession: {
      type: Object,
      default: null
    },
    sessionStats: {
      type: Array,
      required: true,
      default: () => []
    }
  },

  emits: ['session-change', 'player-click'],

  data() {
    return {
      loading: false,
      currentSessionId: null,
      charts: {
        totalDistance: null,
        firstHalf: null,
        secondHalf: null,
        speedZones: null,
        accelDecel: null
      }
    }
  },

  computed: {
    currentSession() {
      if (!this.currentSessionId || !this.sessions.length) return null
      return this.sessions.find(s => s.id === this.currentSessionId) || null
    },

    summaryStats() {
      if (!this.sessionStats.length) {
        return {
          avgDistance: 0,
          sprintDistance: 0,
          highIntensity: 0,
          athleteCount: 0
        }
      }

      const count = this.sessionStats.length

      // Use parseFloat to handle strings and extract from all_parameters if needed
      const getNumericValue = (player, field) => {
        let value = player[field]
        // Try all_parameters if main field is empty
        if ((value === null || value === undefined || value === '') && player.all_parameters) {
          const params = typeof player.all_parameters === 'string'
            ? JSON.parse(player.all_parameters)
            : player.all_parameters
          value = params[field]
        }
        return parseFloat(value) || 0
      }

      const totalDistance = this.sessionStats.reduce((sum, p) => sum + getNumericValue(p, 'total_distance'), 0)
      const totalSprintDist = this.sessionStats.reduce((sum, p) => {
        // Use bands 5+6 if available, fallback to band 4 (>90% Vmax sprint)
        const band5 = getNumericValue(p, 'velocity_band5_distance')
        const band6 = getNumericValue(p, 'velocity_band6_distance')
        const band4 = getNumericValue(p, 'velocity_band4_distance')
        return sum + (band5 + band6 > 0 ? band5 + band6 : band4)
      }, 0)
      const totalPlayerLoad = this.sessionStats.reduce((sum, p) => sum + getNumericValue(p, 'total_player_load'), 0)

      return {
        avgDistance: count > 0 ? totalDistance / count : 0,
        sprintDistance: count > 0 ? totalSprintDist / count : 0,
        highIntensity: count > 0 ? totalPlayerLoad / count : 0,
        athleteCount: count
      }
    },

    sortedPlayerStats() {
      return [...this.sessionStats].sort((a, b) => {
        const distA = a.total_distance || 0
        const distB = b.total_distance || 0
        return distB - distA
      })
    },

    playerLabels() {
      return this.sortedPlayerStats.map(p => this.getPlayerName(p))
    },

    // Sorted by first half distance
    sortedByFirstHalf() {
      return [...this.sessionStats].sort((a, b) => {
        const valA = a.first_half_distance || (a.total_distance * 0.48) || 0
        const valB = b.first_half_distance || (b.total_distance * 0.48) || 0
        return valB - valA
      })
    },

    // Sorted by second half distance
    sortedBySecondHalf() {
      return [...this.sessionStats].sort((a, b) => {
        const valA = a.second_half_distance || (a.total_distance * 0.52) || 0
        const valB = b.second_half_distance || (b.total_distance * 0.52) || 0
        return valB - valA
      })
    },

    // Sorted by total speed zone distance
    sortedBySpeedZones() {
      return [...this.sessionStats].sort((a, b) => {
        const valA = (a.velocity_band1_distance || 0) + (a.velocity_band2_distance || 0) +
                     (a.velocity_band3_distance || 0) + (a.velocity_band4_distance || 0) +
                     (a.velocity_band5_distance || 0) + (a.velocity_band6_distance || 0)
        const valB = (b.velocity_band1_distance || 0) + (b.velocity_band2_distance || 0) +
                     (b.velocity_band3_distance || 0) + (b.velocity_band4_distance || 0) +
                     (b.velocity_band5_distance || 0) + (b.velocity_band6_distance || 0)
        return valB - valA
      })
    },

    // Sorted by total accel/decel events
    sortedByAccelDecel() {
      return [...this.sessionStats].sort((a, b) => {
        const accelA = (a.total_accelerations || a.high_acceleration_count ||
                       ((a.accel_band1_count || 0) + (a.accel_band2_count || 0) + (a.accel_band3_count || 0)))
        const decelA = (a.total_decelerations || a.high_deceleration_count ||
                       ((a.decel_band1_count || 0) + (a.decel_band2_count || 0) + (a.decel_band3_count || 0)))
        const accelB = (b.total_accelerations || b.high_acceleration_count ||
                       ((b.accel_band1_count || 0) + (b.accel_band2_count || 0) + (b.accel_band3_count || 0)))
        const decelB = (b.total_decelerations || b.high_deceleration_count ||
                       ((b.decel_band1_count || 0) + (b.decel_band2_count || 0) + (b.decel_band3_count || 0)))
        return (accelB + decelB) - (accelA + decelA)
      })
    },

    chartColors() {
      return {
        primary: 'rgba(102, 126, 234, 0.85)',
        primaryDark: 'rgba(118, 75, 162, 0.85)',
        success: 'rgba(40, 167, 69, 0.85)',
        warning: 'rgba(255, 193, 7, 0.85)',
        danger: 'rgba(220, 53, 69, 0.85)',
        info: 'rgba(23, 162, 184, 0.85)',
        secondary: 'rgba(108, 117, 125, 0.85)',
        orange: 'rgba(253, 126, 20, 0.85)',
        grid: 'rgba(255, 255, 255, 0.1)',
        label: 'rgba(255, 255, 255, 0.7)'
      }
    },

    chartHeight() {
      // Dynamic height: 55px per player, minimum 300px
      const playerCount = this.sessionStats.length
      const calculatedHeight = playerCount * 55
      return Math.max(300, calculatedHeight)
    }
  },

  watch: {
    sessions: {
      immediate: true,
      handler(newSessions) {
        if (newSessions && newSessions.length && !this.currentSessionId) {
          this.currentSessionId = this.selectedSession?.id || newSessions[0].id
        }
      }
    },

    selectedSession: {
      immediate: true,
      handler(newSession) {
        if (newSession && newSession.id !== this.currentSessionId) {
          this.currentSessionId = newSession.id
        }
      }
    },

    sessionStats: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.renderAllCharts()
        })
      }
    }
  },

  mounted() {
    if (this.sessionStats.length) {
      this.$nextTick(() => {
        this.renderAllCharts()
      })
    }
  },

  beforeUnmount() {
    this.destroyAllCharts()
  },

  methods: {
    onSessionChange() {
      const session = this.sessions.find(s => s.id === this.currentSessionId)
      if (session) {
        this.$emit('session-change', session)
      }
    },

    handlePlayerClick(playerIndex) {
      const player = this.sortedPlayerStats[playerIndex]
      if (player) {
        this.$emit('player-click', player)
      }
    },

    getPlayerName(player) {
      if (player.first_name && player.last_name) {
        return `${player.first_name} ${player.last_name}`
      }
      return player.athlete_name || player.name || 'Unknown'
    },

    formatSessionLabel(session) {
      const date = session.start_time ? new Date(session.start_time) : new Date()
      const dateStr = date.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      })
      const type = session.activity_type === 'match' ? 'Match' : 'Training'
      const name = session.activity_name || ''
      return `${dateStr} - ${type}${name ? ': ' + name : ''}`
    },

    formatDistance(meters) {
      if (!meters) return '-'
      if (meters >= 1000) {
        return (meters / 1000).toFixed(2) + ' km'
      }
      return Math.round(meters) + ' m'
    },

    formatNumber(num) {
      if (!num) return '-'
      return Number(num).toFixed(1)
    },

    destroyAllCharts() {
      Object.keys(this.charts).forEach(key => {
        if (this.charts[key]) {
          this.charts[key].destroy()
          this.charts[key] = null
        }
      })
    },

    renderAllCharts() {
      this.destroyAllCharts()

      if (!this.sessionStats.length) return

      this.$nextTick(() => {
        this.renderTotalDistanceChart()
        this.renderFirstHalfChart()
        this.renderSecondHalfChart()
        this.renderSpeedZonesChart()
        this.renderAccelDecelChart()
      })
    },

    getBaseChartOptions(onClick = null) {
      const self = this
      return {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        animation: false, // Disable animations to prevent canvas context errors
        onClick: onClick ? (evt, elements) => {
          if (elements.length > 0) {
            onClick(elements[0].index)
          }
        } : undefined,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: 'rgba(45, 49, 66, 0.95)',
            titleColor: '#fff',
            bodyColor: '#adb5bd',
            borderColor: 'rgba(102, 126, 234, 0.5)',
            borderWidth: 1,
            padding: 12,
            cornerRadius: 8
          },
          datalabels: {
            anchor: 'end',
            align: 'end',
            color: '#fff',
            font: {
              size: 11,
              weight: '600'
            },
            formatter: (value) => {
              if (value === null || value === undefined) return ''
              return value.toFixed(2) + ' km'
            },
            padding: {
              left: 6
            }
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            grid: {
              color: this.chartColors.grid
            },
            ticks: {
              color: this.chartColors.label,
              font: { size: 11 }
            }
          },
          y: {
            grid: {
              display: false
            },
            ticks: {
              color: this.chartColors.label,
              font: { size: 11 },
              callback: function(value, index) {
                const label = this.getLabelForValue(value)
                return label.length > 15 ? label.substring(0, 15) + '...' : label
              }
            }
          }
        }
      }
    },

    renderTotalDistanceChart() {
      const canvas = this.$refs.totalDistanceCanvas
      if (!canvas || !canvas.getContext) return

      // Destroy existing chart first to prevent canvas reuse error
      if (this.charts.totalDistance) {
        this.charts.totalDistance.destroy()
        this.charts.totalDistance = null
      }

      // Verify canvas context is valid
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const data = this.sortedPlayerStats.map(p => (p.total_distance || 0) / 1000)

      this.charts.totalDistance = new Chart(canvas, {
        type: 'bar',
        data: {
          labels: this.playerLabels,
          datasets: [{
            label: 'Total Distance (km)',
            data: data,
            backgroundColor: this.chartColors.primary,
            borderColor: 'rgba(102, 126, 234, 1)',
            borderWidth: 1,
            borderRadius: 4,
            barThickness: 32,
            categoryPercentage: 0.85,
            barPercentage: 0.9
          }]
        },
        options: {
          ...this.getBaseChartOptions((index) => this.handlePlayerClick(index)),
          scales: {
            ...this.getBaseChartOptions().scales,
            x: {
              ...this.getBaseChartOptions().scales.x,
              title: {
                display: true,
                text: 'Distance (km)',
                color: this.chartColors.label
              }
            }
          }
        }
      })
    },

    renderFirstHalfChart() {
      const canvas = this.$refs.firstHalfCanvas
      if (!canvas || !canvas.getContext) return

      // Destroy existing chart first to prevent canvas reuse error
      if (this.charts.firstHalf) {
        this.charts.firstHalf.destroy()
        this.charts.firstHalf = null
      }

      // Verify canvas context is valid
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const sorted = this.sortedByFirstHalf
      const labels = sorted.map(p => this.getPlayerName(p))
      const data = sorted.map(p => (p.first_half_distance || p.total_distance * 0.48 || 0) / 1000)

      this.charts.firstHalf = new Chart(canvas, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [{
            label: 'First Half Distance (km)',
            data: data,
            backgroundColor: this.chartColors.success,
            borderColor: 'rgba(40, 167, 69, 1)',
            borderWidth: 1,
            borderRadius: 4,
            barThickness: 32,
            categoryPercentage: 0.85,
            barPercentage: 0.9
          }]
        },
        options: {
          ...this.getBaseChartOptions((index) => this.$emit('player-click', sorted[index])),
          scales: {
            ...this.getBaseChartOptions().scales,
            x: {
              ...this.getBaseChartOptions().scales.x,
              title: {
                display: true,
                text: 'Distance (km)',
                color: this.chartColors.label
              }
            }
          }
        }
      })
    },

    renderSecondHalfChart() {
      const canvas = this.$refs.secondHalfCanvas
      if (!canvas || !canvas.getContext) return

      // Destroy existing chart first to prevent canvas reuse error
      if (this.charts.secondHalf) {
        this.charts.secondHalf.destroy()
        this.charts.secondHalf = null
      }

      // Verify canvas context is valid
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const sorted = this.sortedBySecondHalf
      const labels = sorted.map(p => this.getPlayerName(p))
      const data = sorted.map(p => (p.second_half_distance || p.total_distance * 0.52 || 0) / 1000)

      this.charts.secondHalf = new Chart(canvas, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [{
            label: 'Second Half Distance (km)',
            data: data,
            backgroundColor: this.chartColors.info,
            borderColor: 'rgba(23, 162, 184, 1)',
            borderWidth: 1,
            borderRadius: 4,
            barThickness: 32,
            categoryPercentage: 0.85,
            barPercentage: 0.9
          }]
        },
        options: {
          ...this.getBaseChartOptions((index) => this.$emit('player-click', sorted[index])),
          scales: {
            ...this.getBaseChartOptions().scales,
            x: {
              ...this.getBaseChartOptions().scales.x,
              title: {
                display: true,
                text: 'Distance (km)',
                color: this.chartColors.label
              }
            }
          }
        }
      })
    },

    renderSpeedZonesChart() {
      const canvas = this.$refs.speedZonesCanvas
      if (!canvas || !canvas.getContext) return

      // Destroy existing chart first to prevent canvas reuse error
      if (this.charts.speedZones) {
        this.charts.speedZones.destroy()
        this.charts.speedZones = null
      }

      // Verify canvas context is valid
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const sorted = this.sortedBySpeedZones
      const labels = sorted.map(p => this.getPlayerName(p))

      const datasets = [
        {
          label: 'Walking (Band 1)',
          data: sorted.map(p => (p.velocity_band1_distance || 0) / 1000),
          backgroundColor: this.chartColors.secondary,
          borderColor: 'rgba(108, 117, 125, 1)',
          borderWidth: 1,
          barThickness: 32
        },
        {
          label: 'Jogging (Band 2)',
          data: sorted.map(p => (p.velocity_band2_distance || 0) / 1000),
          backgroundColor: this.chartColors.info,
          borderColor: 'rgba(23, 162, 184, 1)',
          borderWidth: 1,
          barThickness: 32
        },
        {
          label: 'Running (Band 3)',
          data: sorted.map(p => (p.velocity_band3_distance || 0) / 1000),
          backgroundColor: this.chartColors.success,
          borderColor: 'rgba(40, 167, 69, 1)',
          borderWidth: 1,
          barThickness: 32
        },
        {
          label: 'High Speed (Band 4)',
          data: sorted.map(p => (p.velocity_band4_distance || 0) / 1000),
          backgroundColor: this.chartColors.warning,
          borderColor: 'rgba(255, 193, 7, 1)',
          borderWidth: 1,
          barThickness: 32
        },
        {
          label: 'Sprint (Band 5)',
          data: sorted.map(p => (p.velocity_band5_distance || 0) / 1000),
          backgroundColor: this.chartColors.orange,
          borderColor: 'rgba(253, 126, 20, 1)',
          borderWidth: 1,
          barThickness: 32
        },
        {
          label: 'Max Sprint (Band 6)',
          data: sorted.map(p => (p.velocity_band6_distance || 0) / 1000),
          backgroundColor: this.chartColors.danger,
          borderColor: 'rgba(220, 53, 69, 1)',
          borderWidth: 1,
          barThickness: 32
        }
      ]

      this.charts.speedZones = new Chart(canvas, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: datasets
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          animation: false, // Disable animations to prevent canvas context errors
          onClick: (evt, elements) => {
            if (elements.length > 0) {
              const player = sorted[elements[0].index]
              this.$emit('player-click', player)
            }
          },
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(45, 49, 66, 0.95)',
              titleColor: '#fff',
              bodyColor: '#adb5bd',
              borderColor: 'rgba(102, 126, 234, 0.5)',
              borderWidth: 1,
              padding: 12,
              cornerRadius: 8,
              callbacks: {
                label: function(context) {
                  return `${context.dataset.label}: ${context.raw.toFixed(2)} km`
                }
              }
            },
            datalabels: {
              display: false
            }
          },
          scales: {
            x: {
              stacked: true,
              beginAtZero: true,
              grid: {
                color: this.chartColors.grid
              },
              ticks: {
                color: this.chartColors.label,
                font: { size: 11 }
              },
              title: {
                display: true,
                text: 'Distance (km)',
                color: this.chartColors.label
              }
            },
            y: {
              stacked: true,
              grid: {
                display: false
              },
              ticks: {
                color: this.chartColors.label,
                font: { size: 11 }
              }
            }
          }
        }
      })
    },

    renderAccelDecelChart() {
      const canvas = this.$refs.accelDecelCanvas
      if (!canvas || !canvas.getContext) return

      // Destroy existing chart first to prevent canvas reuse error
      if (this.charts.accelDecel) {
        this.charts.accelDecel.destroy()
        this.charts.accelDecel = null
      }

      // Verify canvas context is valid
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const sorted = this.sortedByAccelDecel
      const labels = sorted.map(p => this.getPlayerName(p))

      const datasets = [
        {
          label: 'Total Accelerations',
          data: sorted.map(p => {
            // Use total if available, otherwise sum band counts
            if (p.total_accelerations) return p.total_accelerations
            if (p.high_acceleration_count) return p.high_acceleration_count
            // Sum band counts
            return (p.accel_band1_count || 0) + (p.accel_band2_count || 0) + (p.accel_band3_count || 0)
          }),
          backgroundColor: this.chartColors.primary,
          borderColor: 'rgba(102, 126, 234, 1)',
          borderWidth: 1,
          borderRadius: 4,
          barThickness: 14,
          categoryPercentage: 0.85,
          barPercentage: 0.9
        },
        {
          label: 'Total Decelerations',
          data: sorted.map(p => {
            // Use total if available, otherwise sum band counts
            if (p.total_decelerations) return p.total_decelerations
            if (p.high_deceleration_count) return p.high_deceleration_count
            // Sum band counts
            return (p.decel_band1_count || 0) + (p.decel_band2_count || 0) + (p.decel_band3_count || 0)
          }),
          backgroundColor: this.chartColors.primaryDark,
          borderColor: 'rgba(118, 75, 162, 1)',
          borderWidth: 1,
          borderRadius: 4,
          barThickness: 14,
          categoryPercentage: 0.85,
          barPercentage: 0.9
        }
      ]

      this.charts.accelDecel = new Chart(canvas, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: datasets
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          animation: false, // Disable animations to prevent canvas context errors
          onClick: (evt, elements) => {
            if (elements.length > 0) {
              const player = sorted[elements[0].index]
              this.$emit('player-click', player)
            }
          },
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(45, 49, 66, 0.95)',
              titleColor: '#fff',
              bodyColor: '#adb5bd',
              borderColor: 'rgba(102, 126, 234, 0.5)',
              borderWidth: 1,
              padding: 12,
              cornerRadius: 8
            },
            datalabels: {
              anchor: 'end',
              align: 'end',
              color: '#fff',
              font: {
                size: 10,
                weight: '600'
              },
              formatter: (value) => {
                if (value === null || value === undefined || value === 0) return ''
                return Math.round(value)
              },
              padding: {
                left: 4
              }
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              grid: {
                color: this.chartColors.grid
              },
              ticks: {
                color: this.chartColors.label,
                font: { size: 11 }
              },
              title: {
                display: true,
                text: 'Count',
                color: this.chartColors.label
              }
            },
            y: {
              grid: {
                display: false
              },
              ticks: {
                color: this.chartColors.label,
                font: { size: 11 }
              }
            }
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.gps-session-dashboard {
  padding: var(--space-lg, 24px);
}

/* Session Selector */
.session-selector {
  display: flex;
  gap: var(--space-md, 16px);
  margin-bottom: var(--space-lg, 24px);
  flex-wrap: wrap;
}

.selector-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 300px;
}

.selector-group label {
  font-size: 0.75rem;
  color: var(--color-text-muted, #6c757d);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.selector-group select {
  padding: 0.6rem 0.8rem;
  border: 1px solid var(--color-border, #404258);
  border-radius: var(--radius-md, 8px);
  background: var(--color-surface, #2d3142);
  color: var(--color-text, #fff);
  font-size: 0.9rem;
  cursor: pointer;
  transition: border-color var(--transition-fast, 150ms);
}

.selector-group select:hover {
  border-color: var(--color-primary, #667eea);
}

.selector-group select:focus {
  outline: none;
  border-color: var(--color-primary, #667eea);
  box-shadow: 0 0 0 2px var(--color-primary-bg, rgba(102, 126, 234, 0.15));
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: var(--space-2xl, 48px);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border, #404258);
  border-top-color: var(--color-primary, #667eea);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-md, 16px);
  margin-bottom: var(--space-xl, 32px);
}

.summary-card {
  background: var(--color-surface, #2d3142);
  border: 1px solid var(--color-border, #404258);
  border-radius: var(--radius-lg, 12px);
  padding: var(--space-md, 16px);
  display: flex;
  align-items: center;
  gap: var(--space-md, 16px);
  transition: border-color var(--transition-fast, 150ms);
}

.summary-card:hover {
  border-color: var(--color-primary, #667eea);
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md, 8px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: white;
  flex-shrink: 0;
}

.bg-success { background: var(--color-success, #28a745); }
.bg-warning { background: var(--color-warning, #ffc107); color: #333 !important; }
.bg-danger { background: var(--color-danger, #dc3545); }
.bg-info { background: var(--color-info, #17a2b8); }

.card-content {
  display: flex;
  flex-direction: column;
}

.card-content .value {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text, #fff);
}

.card-content .label {
  font-size: 0.75rem;
  color: var(--color-text-muted, #6c757d);
}

/* Charts Section */
.charts-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-md, 16px);
}

.chart-card {
  background: var(--color-surface, #2d3142);
  border: 1px solid var(--color-border, #404258);
  border-radius: var(--radius-lg, 12px);
  padding: var(--space-md, 16px);
}

.chart-card-wide {
  grid-column: span 3;
}

.chart-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0 0 var(--space-md, 16px) 0;
  color: var(--color-text, #fff);
}

.chart-title i {
  color: var(--color-primary-light, #7c8ef5);
}

.chart-container {
  min-height: 250px;
  position: relative;
}

.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-md, 16px);
  margin-bottom: var(--space-md, 16px);
  padding: var(--space-sm, 8px) var(--space-md, 16px);
  background: var(--color-surface-alt, #282b33);
  border-radius: var(--radius-md, 8px);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: var(--color-text-secondary, #adb5bd);
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: var(--space-2xl, 48px);
  color: var(--color-text-muted, #6c757d);
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: var(--space-md, 16px);
  display: block;
}

.empty-state p {
  margin: 0;
  font-size: 1rem;
}

/* Responsive */
@media (max-width: 1200px) {
  .charts-section {
    grid-template-columns: repeat(2, 1fr);
  }

  .chart-card-wide {
    grid-column: span 2;
  }
}

@media (max-width: 900px) {
  .summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }

  .charts-section {
    grid-template-columns: 1fr;
  }

  .chart-card-wide {
    grid-column: span 1;
  }
}

@media (max-width: 600px) {
  .summary-cards {
    grid-template-columns: 1fr;
  }

  .selector-group {
    min-width: 100%;
  }
}
</style>
